import Model, { hasMany, belongsTo, attr } from '@ember-data/model';
import { isBlank, typeOf } from '@ember/utils';
import { A, isArray } from '@ember/array';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { computed, get } from '@ember/object';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { equal } from '@ember/object/computed';
import { service } from '@ember/service';
import { modelValidator } from 'ember-model-validator';
import { LANGUAGE_CODE_ALIASES } from 'invite-sign/helpers/translator';
import { isValidDate } from 'invite-sign/utils/date-format';
import { sortByPosition } from 'invite-sign/utils/sorting';

@modelValidator
class SignInField extends Model {
  @service intl;
  @service localization;

  @belongsTo('invite', { async: true, inverse: 'signInFields' }) invite;
  @belongsTo('group-invite-link', { async: true, inverse: 'signInFields' }) groupInviteLink;
  @belongsTo('entry', { async: true, inverse: 'signInFields' }) entry; // manyToOne this won't be serialized on the entry model
  @belongsTo('signInFieldPage', { async: true, inverse: 'signInFields' }) signInFieldPage;
  @hasMany('sign-in-field', { async: false, inverse: 'actionableSignInFields' }) actionableSignInFields;
  @hasMany('sign-in-field-action', { async: false, inverse: 'actionableSignInField' }) actionableSignInFieldActions;
  @hasMany('sign-in-field-action', { async: false, inverse: 'signInField' }) signInFieldActions;

  @attr('string') value;
  @attr('string') type;
  @attr('string') kind;
  /**
   * @type {String}
   */
  @attr('string') identifier;
  @attr('string') label;
  @attr('number') position;
  @attr('boolean', { defaultValue: false }) required;
  @attr() isTextarea;
  @attr() items;
  @attr('array') ipadLocalizedNames;
  @attr('boolean', { defaultValue: false }) isDisabled;
  @attr('string') dateFormat;
  @attr() customTranslations;

  @equal('identifier', 'name') isName;
  @equal('identifier', 'host') isHost;
  @equal('identifier', 'email') isEmail;
  @equal('identifier', 'locality') isLocality;
  @equal('identifier', 'locality-address') isFullAddress;
  @equal('kind', 'single-selection') isDropdown;
  @equal('kind', 'date') isDate;
  @equal('type', 'Purpose of Visit') isPurposeOfVisit;

  // eslint-disable-next-line ember/classic-decorator-hooks
  init() {
    super.init(...arguments);
    this.validations = {
      value: {
        custom: [
          {
            validation: (key, value, model) => !(model.required && isBlank(model.value)),
            message: 'this_is_a_required_field',
          },
          {
            validation: (key, value, model) => {
              if (!model.isDate || isBlank(model.value)) return true;
              if (!/^\d{4}-\d{2}-\d{2}$/.test(model.value)) return false;
              const [year, month, day] = model.value.split('-').map(Number);
              return isValidDate(day, month, year);
            },
            message: 'invalid_date',
          },
        ],
        ...(this.isEmail
          ? {
              email: {
                if: (key, value, model) => !model.isDisabled,
                allowBlank: !this.required,
                message: 'invalid_email_address',
              },
            }
          : {}),
      },
    };
  }

  /**
   * @return {Boolean}
   */
  @computed('actionableSignInFieldActions.[]')
  get isParent() {
    // eslint-disable-next-line ember/no-get
    return get(this, 'actionableSignInFieldActions.length') === 0;
  }

  /**
   * @return {Boolean}
   */
  @computed('isParent', 'actionableSignInFields.@each.value', 'actionableSignInFieldActions.[]')
  get isVisibleChild() {
    if (!this.isParent) {
      const field = this.actionableSignInFields.slice()[0] || {};
      const { value } = field;

      if (isBlank(value)) {
        return false;
      }

      const action = this.actionableSignInFieldActions.slice()[0];

      return this.extractMatchingParentValues(action.dropdownOption).includes(value);
    }

    return false;
  }

  /**
   * @return {Boolean}
   */
  @computed('isParent', 'isVisibleChild')
  get isVisible() {
    return this.isParent || this.isVisibleChild;
  }

  /**
   * @return {Boolean}
   */
  @computed('isHost', 'isEmail', 'isPurposeOfVisit')
  get renderable() {
    return !this.isHost && !this.isEmail && !this.isPurposeOfVisit;
  }

  /**
   * @description Does this field render for Generic Invite Link landing page?
   * @return {Boolean}
   */
  @computed('renderable', 'isName')
  get selfServiceable() {
    return this.renderable && !this.isName;
  }

  @computed('ipadLocalizedNames', 'customTranslations.name')
  get localizedNames() {
    const { ipadLocalizedNames, customTranslations } = this;

    if (ipadLocalizedNames?.length) return ipadLocalizedNames;

    const translations = customTranslations?.name ?? {};
    const result = Object.keys(translations).map((key) => {
      return { languageCode: key, displayName: translations[key] };
    });

    return result;
  }

  /**
   * @return {Array<Object>}
   */
  @computed('items.@each.{position,value}', 'localization.languageCode')
  get itemsValues() {
    const {
      items,
      localization: { languageCode },
    } = this;

    if (!items || typeof items[0] !== 'object') {
      return items;
    }

    const lowerLanguageCode = languageCode.toLowerCase();
    const aliases = LANGUAGE_CODE_ALIASES[lowerLanguageCode] || [];
    const codes = [languageCode, ...aliases];

    return items.sort(sortByPosition).map((item) => {
      const { value } = item;
      const customTranslations = item.customTranslations?.value ?? {};

      // Build case-insensitive lookup map
      const lowerKeyMap = Object.keys(customTranslations).reduce((map, key) => {
        map[key.toLowerCase()] = key;
        return map;
      }, {});

      const matchingKey = codes.find((c) => lowerKeyMap[c.toLowerCase()]);
      const translated = matchingKey ? customTranslations[lowerKeyMap[matchingKey.toLowerCase()]]?.trim() : null;

      return translated || value;
    });
  }

  extractMatchingParentValues(option) {
    const values = A([option.value]);

    if (isArray(option.ipadLocalizedNames)) {
      values.pushObjects(option.ipadLocalizedNames.map((ipadLocalizedName) => ipadLocalizedName['display-name']));
    }

    if (typeOf(option.customTranslations?.value) === 'object') {
      values.pushObjects(Object.values(option.customTranslations.value));
    }

    return values;
  }
}

export default SignInField;
